/* THEBLACKBOXHUB — product.js
   Variant picker, product info updates, media gallery, lightbox, deferred media,
   sticky add-to-cart, recommendations, recently viewed and quick add. */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const utils = window.themeUtils || {};

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  function money(cents) {
    return utils.formatMoney ? utils.formatMoney(cents) : String(cents / 100);
  }

  function esc(str) {
    return utils.escapeHtml ? utils.escapeHtml(str) : String(str);
  }

  function rootUrl() {
    return (theme.routes.root || '/').replace(/\/$/, '');
  }

  function renderPriceHTML(variant, badgeType) {
    if (!variant) return null;
    const price = variant.price;
    const compare = variant.compare_at_price || 0;
    const strings = theme.strings || {};
    let html = '<div class="price price--large">';
    if (compare > price) {
      html +=
        '<span class="visually-hidden">' + esc(strings.sale || 'Sale') + '</span>' +
        '<span class="price__sale">' + money(price) + '</span>' +
        '<s class="price__compare">' + money(compare) + '</s>';
      if (badgeType && badgeType !== 'none') {
        let label = strings.sale || 'Sale';
        if (badgeType === 'percent') {
          const pct = Math.round(((compare - price) * 100) / compare);
          label = (strings.save || 'Save [amount]').replace('[amount]', pct + '%');
        } else if (badgeType === 'amount') {
          label = (strings.save || 'Save [amount]').replace('[amount]', money(compare - price));
        }
        html += '<span class="badge badge--sale">' + esc(label) + '</span>';
      }
    } else {
      html += '<span class="price__regular">' + money(price) + '</span>';
    }
    html += '</div>';
    return html;
  }

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const data = this.querySelector('script[data-variants]');
      try {
        this.variants = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.optionCount = this.querySelectorAll('[data-option-index]').length;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability(this.getSelectedOptions());
    }

    getSelectedOptions() {
      const values = [];
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        if (group.tagName === 'SELECT') {
          values[index] = group.value;
        } else {
          const checked = group.querySelector('input:checked');
          values[index] = checked ? checked.value : null;
        }
      });
      return values;
    }

    findVariant(selected) {
      return (
        this.variants.find((v) => v.options.every((value, i) => value === selected[i])) || null
      );
    }

    onChange() {
      const selected = this.getSelectedOptions();
      const variant = this.findVariant(selected);
      this.updateLabels(selected);
      this.updateAvailability(selected);
      this.currentVariant = variant;
      this.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variant, selected: selected },
        })
      );
    }

    updateLabels(selected) {
      this.querySelectorAll('[data-selected-value]').forEach((el) => {
        const index = parseInt(el.dataset.selectedValue, 10);
        el.textContent = selected[index] || '';
      });
    }

    updateAvailability(selected) {
      const strings = theme.strings || {};
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        const previous = selected.slice(0, index);
        const candidates = this.variants.filter((v) => previous.every((val, i) => v.options[i] === val));
        const isSelect = group.tagName === 'SELECT';
        const inputs = isSelect ? Array.from(group.options) : Array.from(group.querySelectorAll('input'));
        inputs.forEach((input) => {
          const value = input.value;
          const matches = candidates.filter((v) => v.options[index] === value);
          const exists = matches.length > 0;
          const available = matches.some((v) => v.available);
          if (isSelect) {
            const base = input.dataset.label || value;
            input.dataset.label = base;
            let suffix = '';
            if (!exists) suffix = ' — ' + (strings.optionUnavailable || 'Unavailable');
            else if (!available) suffix = ' — ' + (strings.optionSoldOut || 'Sold out');
            input.textContent = base + suffix;
          } else {
            const label = group.querySelector('label[for="' + input.id + '"]');
            if (label) {
              label.classList.toggle('is-sold-out', exists && !available);
              label.classList.toggle('is-unavailable', !exists);
              const sr = label.querySelector('[data-sr-status]');
              if (sr) {
                sr.textContent = !exists
                  ? ', ' + (strings.optionUnavailable || 'Unavailable')
                  : !available
                  ? ', ' + (strings.optionSoldOut || 'Sold out')
                  : '';
              }
            }
          }
        });
      });
    }
  }
  define('variant-picker', VariantPicker);

  /* ---------- Product info: reacts to variant changes ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('variant:change', (event) => this.onVariantChange(event.detail));
    }

    onVariantChange(detail) {
      const variant = detail.variant;
      const strings = theme.strings || {};

      this.querySelectorAll('[data-variant-id-input]').forEach((input) => {
        if (variant) input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      document.querySelectorAll('[data-variant-id-input][form]').forEach((input) => {
        if (variant && this.querySelector('#' + CSS.escape(input.getAttribute('form')))) input.value = variant.id;
      });

      const badgeType = this.dataset.badgeType || 'percent';
      this.querySelectorAll('[data-price-container]').forEach((el) => {
        if (!variant) return;
        const html = renderPriceHTML(variant, el.dataset.showBadge === 'false' ? 'none' : badgeType);
        if (html) el.innerHTML = html;
      });

      const sku = this.querySelector('[data-sku]');
      if (sku) {
        sku.textContent = variant && variant.sku ? variant.sku : '';
        const wrap = sku.closest('[data-sku-wrapper]');
        if (wrap) wrap.hidden = !(variant && variant.sku);
      }

      this.querySelectorAll('[data-add-button]').forEach((button) => {
        const label = button.querySelector('[data-add-text]') || button;
        if (!variant) {
          button.disabled = true;
          button.dataset.unavailable = 'true';
          label.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          button.disabled = true;
          button.dataset.unavailable = 'true';
          label.textContent = strings.soldOut || 'Sold out';
        } else {
          button.disabled = false;
          delete button.dataset.unavailable;
          button.removeAttribute('aria-disabled');
          label.textContent = strings.addToCart || 'Add to cart';
        }
      });

      const dynamic = this.querySelector('[data-dynamic-checkout]');
      if (dynamic) dynamic.hidden = !(variant && variant.available);

      if (variant && this.dataset.updateUrl === 'true' && this.dataset.url) {
        try {
          window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
        } catch (e) {
          /* ignore */
        }
      }

      if (variant && variant.featured_media) {
        const gallery = this.querySelector('media-gallery');
        if (gallery && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(String(variant.featured_media), true);
      }
    }
  }
  define('product-info', ProductInfo);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.slider = this.querySelector('[data-gallery-slider]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('[data-lightbox]');
      if (!this.slider || !this.slides.length) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.thumb, false));
      });

      this.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = btn.hasAttribute('data-gallery-next') ? 1 : -1;
          const next = Math.min(Math.max(this.activeIndex + dir, 0), this.slides.length - 1);
          this.setActiveMedia(this.slides[next].dataset.mediaId, false);
        });
      });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.markActive(this.slides.indexOf(entry.target));
              }
            });
          },
          { root: this.slider, threshold: [0.6] }
        );
        this.slides.forEach((s) => this.io.observe(s));
      }
      this.activeIndex = 0;

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open-lightbox]');
        if (opener) this.openLightbox(parseInt(opener.dataset.openLightbox, 10) || 0, opener);
      });

      if (this.lightbox) {
        this.lightbox.addEventListener('click', (event) => {
          if (event.target.closest('[data-lightbox-close]')) this.closeLightbox();
        });
        this.lightbox.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeLightbox();
        });
        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeLightbox();
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }

    markActive(index) {
      if (index < 0 || index === this.activeIndex) return;
      this.activeIndex = index;
      this.thumbs.forEach((t, i) => {
        if (i === index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(index + 1);
      this.pauseMedia(index);
      const activeThumb = this.thumbs[index];
      const strip = activeThumb && activeThumb.closest('[data-thumb-strip]');
      if (strip && activeThumb) {
        const left = activeThumb.offsetLeft - strip.clientWidth / 2 + activeThumb.clientWidth / 2;
        strip.scrollTo({ left: left, behavior: 'smooth' });
      }
    }

    setActiveMedia(mediaId, fromVariant) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      const slide = this.slides[index];
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      /* slider is position:relative, so offsetLeft is measured from the slider */
      const isRtl = getComputedStyle(this.slider).direction === 'rtl';
      const left = isRtl ? slide.offsetLeft + slide.offsetWidth - this.slider.clientWidth : slide.offsetLeft;
      this.slider.scrollTo({ left: left, behavior: behavior });
      this.activeIndex = index;
      this.pauseMedia(index);
      this.thumbs.forEach((t, i) => {
        if (i === index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(index + 1);
      if (fromVariant && window.innerWidth < 750) {
        const rect = this.getBoundingClientRect();
        if (rect.bottom < 0) this.scrollIntoView({ behavior: behavior, block: 'start' });
      }
    }

    pauseMedia(activeIndex) {
      this.slides.forEach((slide, i) => {
        if (i === activeIndex) return;
        slide.querySelectorAll('video').forEach((v) => {
          try {
            v.pause();
          } catch (e) {
            /* ignore */
          }
        });
        slide.querySelectorAll('iframe').forEach((f) => {
          try {
            f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            f.contentWindow.postMessage('{"method":"pause"}', '*');
          } catch (e) {
            /* ignore */
          }
        });
      });
    }

    openLightbox(index, opener) {
      if (!this.lightbox) return;
      this.lastOpener = opener;
      if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
      else this.lightbox.setAttribute('open', '');
      if (utils.lockScroll) utils.lockScroll();
      const target = this.lightbox.querySelector('[data-lightbox-index="' + index + '"]');
      requestAnimationFrame(() => {
        if (target) target.scrollIntoView({ block: 'start' });
        const close = this.lightbox.querySelector('[data-lightbox-close]');
        if (close) close.focus({ preventScroll: true });
      });
    }

    closeLightbox() {
      if (!this.lightbox) return;
      if (typeof this.lightbox.close === 'function' && this.lightbox.open) this.lightbox.close();
      else this.lightbox.removeAttribute('open');
      if (utils.unlockScroll) utils.unlockScroll();
      if (this.lastOpener) this.lastOpener.focus({ preventScroll: true });
    }
  }
  define('media-gallery', MediaGallery);

  /* ---------- Deferred media (external video / 3D model) ---------- */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const button = this.querySelector('[data-load-media]');
      if (button) button.addEventListener('click', () => this.load());
    }

    load() {
      if (this.loaded) return;
      const template = this.querySelector('template');
      if (!template) return;
      this.loaded = true;
      const content = template.content.firstElementChild
        ? template.content.firstElementChild.cloneNode(true)
        : null;
      if (!content) return;
      const host = this.querySelector('[data-media-host]') || this;
      host.appendChild(content);
      this.classList.add('is-loaded');
      if (content.tagName === 'MODEL-VIEWER' || content.querySelector('model-viewer')) {
        try {
          if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
            window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: function () {} }]);
          }
        } catch (e) {
          /* poster remains visible */
        }
      }
      const video = host.querySelector('video');
      if (video) {
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }
    }
  }
  define('deferred-media', DeferredMedia);

  /* ---------- Sticky add to cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const target = document.getElementById(this.dataset.target);
      if (!target || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', past);
          this.setAttribute('aria-hidden', past ? 'false' : 'true');
          this.querySelectorAll('button, a, input, select').forEach((el) => {
            el.tabIndex = past ? 0 : -1;
          });
        });
      });
      this.io.observe(target);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }
  }
  define('sticky-atc', StickyAtc);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      if (!this.dataset.url) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => {
            if (!r.ok) throw new Error('bad response');
            return r.text();
          })
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.querySelector('[data-product-card]')) {
              this.innerHTML = fresh.innerHTML;
              this.classList.add('is-loaded');
            } else {
              this.hideSection();
            }
          })
          .catch(() => this.hideSection());
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries, observer) => {
            if (entries.some((e) => e.isIntersecting)) {
              observer.disconnect();
              load();
            }
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        io.observe(this);
      } else {
        load();
      }
    }

    hideSection() {
      if (window.Shopify && window.Shopify.designMode) {
        this.querySelectorAll('[data-skeleton]').forEach((el) => el.remove());
        return;
      }
      const section = this.closest('.shopify-section');
      if (section) section.hidden = true;
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'tbbh-recently-viewed';

  class RecentlyViewedTracker extends HTMLElement {
    connectedCallback() {
      const handle = this.dataset.handle;
      if (!handle || !utils.storageGet) return;
      let list = utils.storageGet(RV_KEY, []);
      if (!Array.isArray(list)) list = [];
      list = [handle].concat(list.filter((h) => h !== handle)).slice(0, 12);
      utils.storageSet(RV_KEY, list);
    }
  }
  define('recently-viewed-tracker', RecentlyViewedTracker);

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const limit = parseInt(this.dataset.limit || '4', 10);
      const exclude = this.dataset.exclude || '';
      let list = utils.storageGet ? utils.storageGet(RV_KEY, []) : [];
      if (!Array.isArray(list)) list = [];
      const handles = list.filter((h) => h && h !== exclude).slice(0, limit);
      if (!handles.length) return this.hideSection();

      Promise.all(
        handles.map((handle) =>
          fetch(rootUrl() + '/products/' + encodeURIComponent(handle) + '.js')
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        const valid = products.filter(Boolean);
        const missing = handles.filter((h, i) => !products[i]);
        if (missing.length && utils.storageSet) {
          utils.storageSet(RV_KEY, list.filter((h) => missing.indexOf(h) === -1));
        }
        if (!valid.length) return this.hideSection();
        const grid = this.querySelector('[data-rv-grid]');
        if (!grid) return;
        grid.innerHTML = valid.map((p) => this.cardHTML(p)).join('');
        const section = this.closest('.shopify-section');
        if (section) section.hidden = false;
        this.hidden = false;
      });
    }

    cardHTML(p) {
      const imgSrc = p.featured_image
        ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=600'
        : null;
      const img = imgSrc
        ? '<img src="' + esc(imgSrc) + '" alt="' + esc(p.title) + '" width="600" height="600" loading="lazy">'
        : '<div class="placeholder-block"></div>';
      let price = '<span class="price__regular">' + money(p.price) + '</span>';
      if (p.compare_at_price && p.compare_at_price > p.price) {
        price =
          '<span class="price__sale">' + money(p.price) + '</span><s class="price__compare">' + money(p.compare_at_price) + '</s>';
      } else if (p.price_varies) {
        price = '<span class="price__regular">' + money(p.price_min) + '+</span>';
      }
      const soldOut = !p.available
        ? '<div class="product-card__badges"><span class="badge badge--muted">' + esc(theme.strings.soldOut || 'Sold out') + '</span></div>'
        : '';
      return (
        '<li><div class="product-card">' +
        '<div class="product-card__media"><div class="media media--hover-zoom">' + img + '</div>' + soldOut + '</div>' +
        '<div class="product-card__info"><h3 class="product-card__title"><a class="product-card__link" href="' +
        esc(p.url) + '">' + esc(p.title) + '</a></h3><div class="price">' + price + '</div></div>' +
        '</div></li>'
      );
    }

    hideSection() {
      if (window.Shopify && window.Shopify.designMode) {
        const empty = this.querySelector('[data-rv-empty]');
        if (empty) empty.hidden = false;
        this.hidden = false;
        return;
      }
      const section = this.closest('.shopify-section');
      if (section) section.hidden = true;
    }
  }
  define('recently-viewed', RecentlyViewed);

  /* ---------- Quick add modal ---------- */
  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.panel = this.querySelector('.modal__panel');
      this.content = this.querySelector('[data-modal-content]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-quick-add]');
        if (!trigger) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(trigger.getAttribute('href') || trigger.dataset.url, trigger);
      });
    }

    open(productUrl, trigger) {
      if (!productUrl) return;
      this.trigger = trigger;
      this.content.innerHTML = '';
      this.hidden = false;
      if (utils.lockScroll) utils.lockScroll();
      if (utils.trapFocus) utils.trapFocus(this.panel, this.panel);
      let url;
      try {
        url = new URL(productUrl, window.location.origin);
      } catch (e) {
        window.location.href = productUrl;
        return;
      }
      url.searchParams.set('section_id', 'quick-add-product');
      fetch(url.toString())
        .then((r) => {
          if (!r.ok) throw new Error('bad response');
          return r.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const content = doc.querySelector('[data-quick-add-content]');
          if (!content) throw new Error('no content');
          this.content.innerHTML = content.outerHTML;
          if (utils.trapFocus) {
            const first = this.content.querySelector('input, select, button');
            utils.trapFocus(this.panel, first || this.panel);
          }
        })
        .catch(() => {
          window.location.href = productUrl;
        });
    }

    close(keepTrigger) {
      if (this.hidden) return;
      this.hidden = true;
      this.content.innerHTML = '';
      if (utils.unlockScroll) utils.unlockScroll();
      if (utils.removeTrapFocus) utils.removeTrapFocus(keepTrigger ? null : this.trigger);
    }
  }
  define('quick-add-modal', QuickAddModal);

  /* ---------- Share button ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', () => {
        const url = this.dataset.url || window.location.href;
        if (navigator.share) {
          navigator.share({ url: url, title: document.title }).catch(() => {});
        } else if (navigator.clipboard) {
          navigator.clipboard
            .writeText(url)
            .then(() => utils.showToast && utils.showToast(this.dataset.copied || 'Link copied'))
            .catch(() => {});
        }
      });
    }
  }
  define('share-button', ShareButton);
})();
